import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { useTranslation } from "i18n";
import {
  expandableTextAreaExceedsShowMoreThreshold,
  sanitizeTextForDisplay,
} from "shared/utils/dom/sanitizeExpandableTextAreaHtml";
import { dataTestIds } from "data-testids";

interface Props {
  html: string;
  limit: number;
  contentClassName?: string;
  lineClamp?: number;
}

const rootClassName = "min-w-0 max-w-full [&_p]:min-w-0";

/** Inline `<br>` while collapsed so `-webkit-line-clamp` ellipsis stays on the last clamped line. */
const richTextBaseClassName =
  "[&_a]:text-color-brand-one [&_a]:underline hover:[&_a]:opacity-90 dark:[&_a]:text-color-accent-one [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5";

/** Only when expanded: block `<br>` restores reliable line breaks (collapsed uses line-clamp). */
const richTextExpandedBrClassName = "[&_br]:block";

function measureExceedsLineClamp(el: HTMLElement, lineClamp: number): boolean {
  const lh = parseFloat(getComputedStyle(el).lineHeight);

  if (Number.isNaN(lh) || lh <= 0) {
    return false;
  }

  return el.scrollHeight > lh * lineClamp + 2;
}

const ExpandableSanitizedHtml = ({
  html,
  limit,
  contentClassName,
  lineClamp = 3,
}: Props) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const measureRef = useRef<HTMLDivElement>(null);
  const [measuredLong, setMeasuredLong] = useState(false);

  const safeHtml = useMemo(() => sanitizeTextForDisplay(html), [html]);

  const structuralLong = useMemo(
    () =>
      expandableTextAreaExceedsShowMoreThreshold(safeHtml, limit, lineClamp),
    [safeHtml, limit, lineClamp]
  );

  useLayoutEffect(() => {
    const el = measureRef.current;

    if (!el) {
      return;
    }

    const runMeasure = () => {
      setMeasuredLong(measureExceedsLineClamp(el, lineClamp));
    };

    runMeasure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const ro = new ResizeObserver(runMeasure);
    ro.observe(el);

    return () => ro.disconnect();
  }, [safeHtml, contentClassName, lineClamp]);

  const isLong = structuralLong || measuredLong;

  const collapsedRichTextClassName = cx(
    richTextBaseClassName,
    contentClassName
  );

  const expandedRichTextClassName = cx(
    richTextBaseClassName,
    richTextExpandedBrClassName,
    contentClassName
  );

  return (
    <div className={rootClassName}>
      <div className="relative w-full min-w-0 overflow-hidden">
        <div
          ref={measureRef}
          className={cx(
            collapsedRichTextClassName,
            "pointer-events-none absolute left-0 right-0 top-0 h-0 w-full overflow-hidden opacity-0"
          )}
          aria-hidden
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        <div
          className={cx(
            isExpanded ? expandedRichTextClassName : collapsedRichTextClassName,
            "relative z-10 w-full",
            isLong && !isExpanded && "overflow-hidden"
          )}
          style={
            isLong && !isExpanded
              ? {
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: lineClamp,
                }
              : undefined
          }
          dangerouslySetInnerHTML={{ __html: safeHtml }}
          data-testid={
            dataTestIds.profile["Profile.ExpandableText.textContent"]
          }
        />
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 text-s font-bold text-color-brand-one hover:underline dark:text-color-accent-one"
        >
          {isExpanded
            ? t("common:button.label.less")
            : t("common:button.label.more.lower")}
        </button>
      )}
    </div>
  );
};

export default ExpandableSanitizedHtml;
